<template>
  <div class="flexben-container widget-carousel-create-update">
    <div class="row mb-3">
      <div
          class="col-12 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center"
      >
        <div class="mb-2 mb-md-0">
          <h1 class="mb-0">{{ pageTitle }}</h1>
        </div>

        <div class="d-flex flex-wrap gap-2">
          <button
              type="button"
              class="btn btn-outline-secondary btn-sm mr-2"
              @click="close"
          >
            Înapoi
          </button>

          <button
              type="button"
              class="btn btn-primary btn-sm mr-2"
              :disabled="loading || !canSubmit"
              @click="submit"
          >
            {{ loading ? "Se salvează..." : "Salvează" }}
          </button>

          <button
              v-if="formAction === 'update'"
              type="button"
              class="btn btn-sm"
              :class="publishButtonClass"
              :disabled="loading || !canPublish"
              @click="publish"
          >
            {{ publishButtonText }}
          </button>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="container-bg-white widget-carousel-create-update__container">
          <div class="row">
            <div class="col-12 col-lg-7">
              <WidgetCarouselTabs />
            </div>

            <div class="col-12 col-lg-5 mt-3 mt-lg-0">
              <div class="widget-carousel-create-update__preview-sticky">
                <WidgetCarouselPreview :form="form" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <content-loader
        :loader-visible="loading"
        :loader-error="loaderError"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import widgetCarouselStore from "./store/widgetCarousel";

import WidgetCarouselPreview from "./components/WidgetCarouselPreview.vue";
import WidgetCarouselTabs from "./components/WidgetCarouselTabs.vue";

import HttpWidgetServices from "@/superUp-module/resources/services/httpWidgetServices";
import { buildWidgetCarouselCreateFormData } from "./utils/widgetCarouselSubmit";

const STORE_NAMESPACE = "widgetCarousel";

export default {
  name: "WidgetCreateUpdateCarousel",

  components: {
    WidgetCarouselPreview,
    WidgetCarouselTabs
  },

  props: {
    id: {
      type: [String, Number],
      default: null
    },
    formAction: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      loading: false,
      loaderError: false
    };
  },

  computed: {
    ...mapGetters(STORE_NAMESPACE, [
      "form",
      "isCreateMode",
      "isUpdateMode"
    ]),

    pageTitle() {
      return this.isUpdateMode
          ? "Update widget carousel"
          : "Create widget carousel";
    },

    canSubmit() {
      return !!(this.form && this.form.widget_id && String(this.form.widget_id).trim());
    },

    canPublish() {
      return !!(this.id && this.formAction === "update");
    },

    nextPublishState() {
      return !(this.form && this.form.is_published);
    },

    publishButtonText() {
      if (this.loading) {
        return this.form && this.form.is_published
            ? "Se dezactivează..."
            : "Se activează...";
      }

      return this.form && this.form.is_published
          ? "Deactivate for All users"
          : "Activate for All users";
    },

    publishButtonClass() {
      return this.form && this.form.is_published
          ? "btn-outline-danger"
          : "btn-success";
    }
  },

  created() {
    this.registerStoreModule();
    this.initialize();
  },

  beforeDestroy() {
    this.unregisterStoreModule();
  },

  methods: {
    async publish() {
      if (!this.canPublish) {
        return;
      }

      this.loading = true;
      this.loaderError = false;

      try {
        await HttpWidgetServices.publish(this.id, this.nextPublishState);

        this.$store.dispatch(`${STORE_NAMESPACE}/updateField`, {
          field: "is_published",
          value: this.nextPublishState
        });
      } catch (error) {
        this.loaderError = true;
      } finally {
        this.loading = false;
      }
    },

    registerStoreModule() {
      if (!this.$store.state[STORE_NAMESPACE]) {
        this.$store.registerModule(STORE_NAMESPACE, widgetCarouselStore);
      }
    },

    unregisterStoreModule() {
      if (this.$store.state[STORE_NAMESPACE]) {
        this.$store.unregisterModule(STORE_NAMESPACE);
      }
    },

    initialize() {
      if (this.formAction === "update") {
        this.initializeUpdate();
        return;
      }

      this.$store.dispatch(`${STORE_NAMESPACE}/initCreate`);
    },

    initializeUpdate() {
      this.loading = true;
      this.loaderError = false;

      HttpWidgetServices.get(this.id)
          .then((response) => {
            const payload =
                response &&
                response.data &&
                response.data.data
                    ? response.data.data
                    : null;

            if (!payload) {
              throw new Error("Invalid widget payload.");
            }

            this.$store.dispatch(`${STORE_NAMESPACE}/initUpdate`, payload);
          })
          .catch(() => {
            this.loaderError = true;
          })
          .finally(() => {
            this.loading = false;
          });
    },

    async submit() {
      if (!this.canSubmit) {
        return;
      }

      this.loading = true;
      this.loaderError = false;

      try {
        const formData = buildWidgetCarouselCreateFormData(this.form);

        if (this.isCreateMode) {
          await HttpWidgetServices.create(formData);
        } else {
          await HttpWidgetServices.update(this.id, formData);
        }

        this.close();
      } catch (error) {
        this.loaderError = true;
      } finally {
        this.loading = false;
      }
    },

    close() {
      this.$emit("update:formAction", null);
    }
  }
};
</script>

<style scoped>
.widget-carousel-create-update h1 {
  font-size: 24px;
  font-weight: 700;
}

.widget-carousel-create-update__container {
  padding: 16px;
}

.widget-carousel-create-update__preview-sticky {
  position: sticky;
  top: 16px;
}

@media (max-width: 991.98px) {
  .widget-carousel-create-update__preview-sticky {
    position: static;
    top: auto;
  }
}

@media (max-width: 767.98px) {
  .widget-carousel-create-update__container {
    padding: 12px;
  }
}
</style>